import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Badge, Modal,
  TextInput, NumberInput, Select, Stack, Loader, Center,
  ActionIcon, Tooltip
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconPencil, IconTrash, IconTarget } from '@tabler/icons-react';

const AREE = [
  { value: 'usave_fco', label: 'U-Save FCO' },
  { value: 'usave_cia', label: 'U-Save CIA' },
  { value: 'commercio_veicoli', label: 'Commercio Veicoli' },
  { value: 'consulenza_nlt', label: 'Consulenza NLT' },
  { value: 'generale', label: 'Generale' },
];

const areaLabel = (val) => AREE.find(a => a.value === val)?.label || val;

function ImpostazioniKPI() {
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    area: 'usave_fco', nome_kpi: '', valore_target: 0,
    soglia_verde: null, soglia_gialla: null, unita: '%', note: ''
  });

  useEffect(() => { loadKPIs(); }, []);

  const loadKPIs = async () => {
    try {
      const res = await api.get('/kpi-targets');
      setKpis(res.data);
    } catch { notifications.show({ title: 'Errore', message: 'Impossibile caricare i KPI', color: 'red' }); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ area: 'usave_fco', nome_kpi: '', valore_target: 0, soglia_verde: null, soglia_gialla: null, unita: '%', note: '' });
  };

  const handleEdit = (kpi) => {
    setEditing(kpi);
    setForm({
      area: kpi.area, nome_kpi: kpi.nome_kpi, valore_target: kpi.valore_target,
      soglia_verde: kpi.soglia_verde, soglia_gialla: kpi.soglia_gialla,
      unita: kpi.unita || '%', note: kpi.note || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/kpi-targets/${editing.id}`, form);
        notifications.show({ title: 'Successo', message: 'KPI aggiornato', color: 'green' });
      } else {
        await api.post('/kpi-targets', form);
        notifications.show({ title: 'Successo', message: 'KPI creato', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      loadKPIs();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel salvataggio', color: 'red' });
    }
  };

  const handleDelete = async (kpi) => {
    if (window.confirm(`Eliminare KPI "${kpi.nome_kpi}"?`)) {
      try {
        await api.delete(`/kpi-targets/${kpi.id}`);
        notifications.show({ title: 'Successo', message: 'KPI eliminato', color: 'green' });
        loadKPIs();
      } catch { notifications.show({ title: 'Errore', message: 'Errore eliminazione', color: 'red' }); }
    }
  };

  if (loading) return <Center h={400}><Loader size="lg" color="teal" /></Center>;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconTarget size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Impostazioni KPI</Title>
        </Group>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { resetForm(); setShowModal(true); }}>
          Nuovo KPI
        </Button>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Area</Table.Th>
              <Table.Th>Nome KPI</Table.Th>
              <Table.Th ta="right">Target</Table.Th>
              <Table.Th ta="right">Soglia Verde</Table.Th>
              <Table.Th ta="right">Soglia Gialla</Table.Th>
              <Table.Th>Unità</Table.Th>
              <Table.Th>Azioni</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {kpis.map(kpi => (
              <Table.Tr key={kpi.id}>
                <Table.Td><Badge variant="subtle" color="teal">{areaLabel(kpi.area)}</Badge></Table.Td>
                <Table.Td><Text fw={600}>{kpi.nome_kpi}</Text></Table.Td>
                <Table.Td ta="right"><Text fw={600}>{kpi.valore_target}</Text></Table.Td>
                <Table.Td ta="right">
                  {kpi.soglia_verde != null ? (
                    <Badge color="green" variant="subtle">≥ {kpi.soglia_verde}</Badge>
                  ) : '-'}
                </Table.Td>
                <Table.Td ta="right">
                  {kpi.soglia_gialla != null ? (
                    <Badge color="yellow" variant="subtle">≥ {kpi.soglia_gialla}</Badge>
                  ) : '-'}
                </Table.Td>
                <Table.Td>{kpi.unita || '-'}</Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Tooltip label="Modifica">
                      <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(kpi)}>
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Elimina">
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(kpi)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {kpis.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}><Text ta="center" c="dimmed" py="xl">Nessun KPI configurato</Text></Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={showModal} onClose={() => setShowModal(false)}
             title={editing ? 'Modifica KPI' : 'Nuovo KPI'} size="md">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Select label="Area" data={AREE} value={form.area}
                    onChange={v => setForm({ ...form, area: v })} required />
            <TextInput label="Nome KPI" required value={form.nome_kpi}
                       onChange={e => setForm({ ...form, nome_kpi: e.target.value })}
                       placeholder="Es: Margine operativo, ROI, etc." />
            <Select label="Unità di misura" data={[
              { value: '%', label: 'Percentuale (%)' },
              { value: '€', label: 'Euro (€)' },
              { value: '', label: 'Valore numerico' },
            ]} value={form.unita} onChange={v => setForm({ ...form, unita: v })} />
            <NumberInput label="Valore Target" required decimalScale={2}
                         value={form.valore_target} onChange={v => setForm({ ...form, valore_target: v || 0 })} />
            <NumberInput label="Soglia Verde (≥ questo valore = semaforo verde)" decimalScale={2}
                         value={form.soglia_verde} onChange={v => setForm({ ...form, soglia_verde: v })} />
            <NumberInput label="Soglia Gialla (≥ questo valore = semaforo giallo, sotto = rosso)" decimalScale={2}
                         value={form.soglia_gialla} onChange={v => setForm({ ...form, soglia_gialla: v })} />
            <TextInput label="Note" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setShowModal(false)}>Annulla</Button>
              <Button type="submit">Salva</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

export default ImpostazioniKPI;
